/**
 * Sistema de Detección Automática de Idioma por País
 * PublikaYA - Multi-language Support
 */

class LanguageDetector {
    constructor() {
        this.supportedLanguages = ['es', 'en', 'fr', 'de'];
        this.defaultLanguage = 'es';
        this.storageKey = 'publishka_user_language_preference';
        
        // Mapeo de países a idiomas
        this.countryToLanguage = {
            // Países de habla inglesa
            'US': 'en', 'GB': 'en', 'CA': 'en', 'AU': 'en', 'NZ': 'en', 'IE': 'en',
            'IN': 'en', 'ZA': 'en', 'SG': 'en', 'MY': 'en', 'PH': 'en',
            
            // Países de habla francesa
            'FR': 'fr', 'BE': 'fr', 'CH': 'fr', 'LU': 'fr', 'MC': 'fr',
            'MA': 'fr', 'DZ': 'fr', 'TN': 'fr', 'SN': 'fr', 'CI': 'fr',
            
            // Países de habla alemana
            'DE': 'de', 'AT': 'de', 'LI': 'de',
            
            // Países de habla española
            'ES': 'es', 'MX': 'es', 'CO': 'es', 'AR': 'es', 'PE': 'es',
            'VE': 'es', 'CL': 'es', 'EC': 'es', 'GT': 'es', 'CU': 'es',
            'BO': 'es', 'DO': 'es', 'HN': 'es', 'PY': 'es', 'SV': 'es',
            'NI': 'es', 'CR': 'es', 'PA': 'es', 'UY': 'es', 'PR': 'es',
            'GQ': 'es'
        };
    }

    /**
     * Obtiene el idioma actual desde la URL
     */
    getCurrentLanguage() {
        const path = window.location.pathname;
        const match = path.match(/^\/(en|fr|de)\//);
        return match ? match[1] : 'es';
    }

    /**
     * Verifica si el usuario ya tiene una preferencia guardada
     */
    getUserPreference() {
        return localStorage.getItem(this.storageKey);
    }

    /**
     * Guarda la preferencia de idioma del usuario
     */
    saveUserPreference(language) {
        localStorage.setItem(this.storageKey, language);
        console.log(`✅ Preferencia de idioma guardada: ${language}`);
    }

    /**
     * Detecta el país del usuario usando geolocalización por IP
     */
    async detectUserCountry() {
        try {
            // Intentar con ipapi.co (gratis, sin API key)
            const response = await fetch('https://ipapi.co/json/', {
                timeout: 3000
            });
            
            if (!response.ok) throw new Error('Error en la respuesta de ipapi.co');
            
            const data = await response.json();
            console.log('🌍 País detectado:', data.country_name, `(${data.country_code})`);
            return {
                countryCode: data.country_code,
                countryName: data.country_name,
                city: data.city,
                region: data.region
            };
        } catch (error) {
            console.warn('⚠️ No se pudo detectar el país automáticamente:', error);
            return null;
        }
    }

    /**
     * Obtiene el idioma recomendado según el país
     */
    getLanguageFromCountry(countryCode) {
        return this.countryToLanguage[countryCode] || this.defaultLanguage;
    }

    /**
     * Detecta el idioma del navegador como fallback
     */
    detectBrowserLanguage() {
        const browserLang = navigator.language || navigator.userLanguage;
        const langCode = browserLang.split('-')[0].toLowerCase();
        
        if (this.supportedLanguages.includes(langCode)) {
            console.log('🌐 Idioma del navegador detectado:', langCode);
            return langCode;
        }
        
        return this.defaultLanguage;
    }

    /**
     * Construye la URL para el idioma especificado
     * Usa el sistema de LanguagePersistence si está disponible
     */
    getLanguageUrl(language) {
        // Usar el nuevo sistema de routing si está disponible
        if (window.LanguagePersistence && window.LanguagePersistence.convertPathToLang) {
            const targetPath = window.LanguagePersistence.convertPathToLang(
                window.location.pathname, 
                language
            );
            if (!targetPath || targetPath === window.location.pathname) return null;
            return targetPath;
        }

        // Fallback al sistema antiguo
        const currentPath = window.location.pathname;
        const currentLang = this.getCurrentLanguage();
        
        // Si ya está en el idioma correcto, no hacer nada
        if (currentLang === language) {
            return null;
        }
        
        // Si está en la raíz (español) y quiere ir a otro idioma
        if (currentLang === 'es' && language !== 'es') {
            // Intentar mantener la ruta actual si no es la raíz
            if (currentPath !== '/') {
                return `/${language}${currentPath}`;
            }
            return `/${language}/`;
        }
        
        // Si está en otro idioma y quiere ir a español
        if (language === 'es') {
            // Quitar prefijo de idioma de la ruta actual
            return currentPath.replace(/^\/(en|fr|de)(\/|$)/, '/');
        }
        
        // Si está en un idioma y quiere ir a otro idioma diferente
        return currentPath.replace(`/${currentLang}/`, `/${language}/`);
    }

    /**
     * Redirige al idioma especificado
     */
    redirectToLanguage(language) {
        const url = this.getLanguageUrl(language);
        if (url) {
            console.log(`🔄 Redirigiendo a: ${url}`);
            window.location.href = url;
        }
    }

    /**
     * Detecta y redirige automáticamente (solo si es primera visita)
     */
    async autoDetectAndRedirect() {
        // Si el usuario ya tiene una preferencia, respetarla
        const userPreference = this.getUserPreference();
        if (userPreference) {
            console.log('✅ Usuario ya tiene preferencia guardada:', userPreference);
            return;
        }

        // Solo hacer la detección en la página de inicio (raíz)
        const isHomePage = window.location.pathname === '/' || 
                          window.location.pathname.match(/^\/(en|fr|de)\/$/);
        
        if (!isHomePage) {
            console.log('📄 No es página de inicio, omitiendo detección automática');
            return;
        }

        console.log('🔍 Detectando país del usuario...');
        
        // Intentar detectar por país primero
        const countryData = await this.detectUserCountry();
        let recommendedLanguage = this.defaultLanguage;
        
        if (countryData && countryData.countryCode) {
            recommendedLanguage = this.getLanguageFromCountry(countryData.countryCode);
            console.log(`🌍 Idioma recomendado según país: ${recommendedLanguage}`);
        } else {
            // Fallback: usar idioma del navegador
            recommendedLanguage = this.detectBrowserLanguage();
            console.log(`🌐 Idioma recomendado según navegador: ${recommendedLanguage}`);
        }

        // Guardar la preferencia automática
        this.saveUserPreference(recommendedLanguage);

        // Redirigir si es necesario
        const currentLanguage = this.getCurrentLanguage();
        if (currentLanguage !== recommendedLanguage) {
            console.log(`🔄 Redirigiendo de ${currentLanguage} a ${recommendedLanguage}`);
            this.redirectToLanguage(recommendedLanguage);
        } else {
            console.log('✅ Ya está en el idioma correcto');
        }
    }

    /**
     * Maneja el cambio manual de idioma (desde el selector)
     */
    handleManualLanguageChange(selectedLanguage) {
        console.log(`👆 Usuario seleccionó manualmente: ${selectedLanguage}`);
        this.saveUserPreference(selectedLanguage);
        this.redirectToLanguage(selectedLanguage);
    }

    /**
     * Inicializa el sistema de detección de idioma
     */
    init() {
        console.log('🚀 Inicializando detector de idioma...');
        
        // Detectar y redirigir automáticamente (muy diferido para no afectar PSI/LCP)
        try {
            const run = () => { try { this.autoDetectAndRedirect(); } catch(_) {} };
            let done = false;
            const runOnce = () => { if (done) return; done = true; run(); };
            // Preferir interacción del usuario
            ['pointerdown','touchstart','keydown','focusin'].forEach(ev => window.addEventListener(ev, runOnce, { once: true, capture: true }));
            // Fallback: ejecutar mucho más tarde
            const scheduleLate = () => setTimeout(runOnce, 20000);
            if (document.readyState === 'complete') scheduleLate();
            else window.addEventListener('load', scheduleLate, { once: true });
        } catch (_) {
            setTimeout(() => { try { this.autoDetectAndRedirect(); } catch(__) {} }, 20000);
        }

        // Escuchar cambios manuales del selector de idiomas
        this.setupLanguageSelector();
    }

    /**
     * Configura los listeners para el selector manual de idiomas
     */
    setupLanguageSelector() {
        // Esperar a que el DOM esté listo
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.attachSelectorListeners());
        } else {
            this.attachSelectorListeners();
        }
    }

    /**
     * Adjunta los event listeners a los selectores de idioma
     * Usa DELEGACIÓN de eventos (un solo listener en el contenedor)
     * para evitar conflictos con otros scripts que modifican el DOM
     */
    attachSelectorListeners() {
        const self = this;
        
        // Delegación: capturamos clicks en el contenedor del selector de idioma
        document.addEventListener('click', function(e) {
            // Buscar si el click (o su target) es un link de idioma
            const langOption = e.target.closest('.language-switcher .lang-option, .lang-switcher a, [data-language]');
            if (!langOption) return;
            
            const href = langOption.getAttribute('href');
            if (!href) return;
            
            // Detectar el idioma del enlace
            let selectedLang = self.defaultLanguage;
            if (href === '/') {
                selectedLang = 'es';
            } else if (href.indexOf('/en') === 0) {
                selectedLang = 'en';
            } else if (href.indexOf('/fr') === 0) {
                selectedLang = 'fr';
            } else if (href.indexOf('/de') === 0) {
                selectedLang = 'de';
            }
            
            // Guardar preferencia ANTES de que el link navegue
            self.saveUserPreference(selectedLang);
            
            // NO hacer preventDefault - el <a href="..."> navega naturalmente
            // El inline redirect en <head> + language-persistence.js manejan el resto
        }, true); // Fase de captura para asegurar que se ejecute
    }
}

// Crear instancia global y auto-inicializar
const languageDetector = new LanguageDetector();

// Inicializar cuando el script se carga
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => languageDetector.init());
} else {
    languageDetector.init();
}

// Exportar para uso manual si es necesario
window.LanguageDetector = languageDetector;
